import { useEffect, useMemo, useState } from 'react'
import { BadgePercent } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

export interface LineDiscountValue {
  mode: 'amount' | 'percent'
  /** Rupees or percent, depending on `mode`. */
  value: number
  /**
   * F-CTRL-N44: manager PIN, required by the server only when the discount
   * comps the line to zero. A full comp is financially identical to voiding
   * the line, so it takes the same authorisation a void does. Undefined for
   * any partial discount.
   */
  pin?: string
}

export interface LineDiscountPopoverProps {
  /** quantity * unit_price for the line — used to cap the input and label the help text. */
  lineSubtotal: number
  /** Currently applied discount, or null when the line is at full price. */
  current: LineDiscountValue | null
  /**
   * Disable the trigger entirely. Used when a promo code is applied on
   * the order (server rejects manual discounts in that state).
   */
  disabled?: boolean
  /** Hint shown when the trigger is disabled. */
  disabledHint?: string
  /** Called when the cashier clicks Apply with a valid value. */
  onApply: (next: LineDiscountValue) => void
  /** Called when the cashier clicks Remove. */
  onRemove: () => void
  /** Optional pending flag — gates Apply while a mutation is in flight. */
  pending?: boolean
}

const fmt = (n: number) =>
  n.toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const round2 = (n: number) => Math.round(n * 100) / 100

/**
 * F-CTRL-N44: does this discount comp the line to zero?
 *
 * A full comp is financially identical to voiding the line, so it takes the
 * same manager PIN a void does. Keyed off the RESOLVED rupee discount rather
 * than "percent === 100", because a flat-Rs discount equal to the line total is
 * the same outcome by another route.
 *
 * Both sides are rounded to paisa before comparing, which absorbs float noise
 * on an odd subtotal — it does NOT stretch to near-comps. 99.999% of 1050 is
 * 1049.99, which leaves a real balance and is deliberately not gated.
 *
 * That boundary is the honest limit of this control: it makes comping a line to
 * zero cost the same manager PIN as voiding it, and nothing more. It does not
 * make discounting authorised in general — a cashier can still take 99% off
 * without a PIN. Setting an approval threshold for discounts as a whole is a
 * separate policy decision, not something to smuggle in behind a rounding rule.
 *
 * Mirrors the server check in handlers.ApplyOrderItemDiscount. The server is
 * authoritative and rejects a missing PIN; this only decides whether to ASK.
 */
export function isFullCompDiscount(resolvedDiscount: number, lineSubtotal: number): boolean {
  if (!(lineSubtotal > 0)) return false
  if (!Number.isFinite(resolvedDiscount)) return false
  return round2(resolvedDiscount) >= round2(lineSubtotal)
}

export function LineDiscountPopover({
  lineSubtotal,
  current,
  disabled,
  disabledHint,
  onApply,
  onRemove,
  pending,
}: LineDiscountPopoverProps) {
  const [open, setOpen] = useState(false)
  const [mode, setMode] = useState<'amount' | 'percent'>(current?.mode ?? 'percent')
  const [value, setValue] = useState<string>(current ? String(current.value) : '')
  const [pin, setPin] = useState('')

  // Sync local editor state when the popover opens — the cashier may have
  // changed the line discount via another path (e.g. after a partial
  // void) and we don't want the stale form value to overwrite it on apply.
  useEffect(() => {
    if (open) {
      setMode(current?.mode ?? 'percent')
      setValue(current ? String(current.value) : '')
      setPin('')
    }
  }, [open, current])

  const parsed = useMemo(() => {
    const n = parseFloat(value)
    return Number.isFinite(n) ? n : NaN
  }, [value])

  const resolvedRs = useMemo(() => {
    if (!Number.isFinite(parsed)) return 0
    return mode === 'percent' ? lineSubtotal * (parsed / 100) : parsed
  }, [parsed, mode, lineSubtotal])

  const error = useMemo<string | null>(() => {
    if (value.trim() === '') return null
    if (!Number.isFinite(parsed) || parsed < 0) return 'Enter a positive number'
    if (mode === 'percent' && parsed > 100) return 'Percent cannot exceed 100'
    if (mode === 'amount' && parsed > lineSubtotal)
      return `Cannot exceed line total Rs ${fmt(lineSubtotal)}`
    return null
  }, [parsed, mode, value, lineSubtotal])

  const isFullComp = isFullCompDiscount(resolvedRs, lineSubtotal)

  const canApply =
    !pending &&
    value.trim() !== '' &&
    Number.isFinite(parsed) &&
    parsed >= 0 &&
    !error &&
    (!isFullComp || pin.length === 4)

  const handleApply = () => {
    if (!canApply) return
    onApply({ mode, value: parsed, pin: isFullComp ? pin : undefined })
    setOpen(false)
  }

  const handleRemove = () => {
    onRemove()
    setOpen(false)
  }

  const valueLabel = current
    ? current.mode === 'percent'
      ? `${current.value}%`
      : `Rs ${fmt(current.value)}`
    : null

  return (
    <Popover open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          title={
            disabled
              ? disabledHint
              : current
                ? `Edit discount (${valueLabel})`
                : 'Apply line discount'
          }
          aria-label={current ? `Edit discount (${valueLabel})` : 'Apply line discount'}
          className={cn(
            'inline-flex items-center justify-center rounded-md border transition-colors',
            current
              ? 'h-6 gap-1 px-1.5 text-[11px] font-medium border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 dark:border-amber-700 dark:bg-amber-950/50 dark:text-amber-200'
              : 'h-6 w-6 border-border bg-background text-muted-foreground hover:bg-muted',
            disabled && 'cursor-not-allowed opacity-50 hover:bg-background',
          )}
        >
          <BadgePercent className="h-3.5 w-3.5" aria-hidden />
          {current && <span>{valueLabel}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64" align="end">
        <div className="space-y-3">
          <div className="flex items-baseline justify-between">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Line discount
            </Label>
            <span className="text-[11px] text-muted-foreground">
              Line Rs {fmt(lineSubtotal)}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-1">
            <Button
              type="button"
              size="sm"
              variant={mode === 'amount' ? 'default' : 'outline'}
              className="h-8 px-2 text-sm"
              onClick={() => setMode('amount')}
              aria-pressed={mode === 'amount'}
            >
              Rs
            </Button>
            <Button
              type="button"
              size="sm"
              variant={mode === 'percent' ? 'default' : 'outline'}
              className="h-8 px-2 text-sm"
              onClick={() => setMode('percent')}
              aria-pressed={mode === 'percent'}
            >
              %
            </Button>
          </div>
          <Input
            inputMode="decimal"
            autoFocus
            className="h-9 text-sm"
            placeholder={mode === 'percent' ? 'e.g. 50' : '0.00'}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && canApply) {
                e.preventDefault()
                handleApply()
              }
            }}
          />
          {value.trim() !== '' && !error && Number.isFinite(parsed) && parsed > 0 && (
            <p className="text-[11px] text-muted-foreground">
              {mode === 'percent'
                ? `= Rs ${fmt(resolvedRs)} off this line`
                : `Off Rs ${fmt(lineSubtotal)} line`}
            </p>
          )}
          {error && <p className="text-[11px] text-destructive">{error}</p>}
          {isFullComp && !error && (
            <div className="space-y-1.5 rounded-md border border-amber-300 bg-amber-50 p-2 dark:border-amber-700 dark:bg-amber-950/40">
              <Label className="text-[11px] font-medium text-amber-900 dark:text-amber-200">
                Manager PIN — comps this line to Rs 0
              </Label>
              <Input
                type="password"
                inputMode="numeric"
                autoComplete="off"
                maxLength={4}
                className="h-8 text-sm tracking-[0.3em]"
                placeholder="••••"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && canApply) {
                    e.preventDefault()
                    handleApply()
                  }
                }}
              />
              <p className="text-[10.5px] leading-snug text-amber-900/80 dark:text-amber-200/80">
                Zeroing a line is the same as voiding it, so it needs the same approval.
              </p>
            </div>
          )}
          <div className="flex items-center gap-2 pt-1">
            <Button
              type="button"
              size="sm"
              className="h-8 flex-1"
              disabled={!canApply}
              onClick={handleApply}
            >
              Apply
            </Button>
            {current && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-8 px-2 text-xs"
                onClick={handleRemove}
              >
                Remove
              </Button>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
